/**
 * Relay settings across navigation (e.g. for redirect)
 * @module
 */
var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var Cookie = require('../utils/cookie');
var CoreApiEvents = require('core/api_events');
var di = require('../di');
var enums = require('../constants/enums');
var Flux = require('../flux');
var Logger = require('../utils/logger');
var trackingPolicies = require('../constants/tracking_policies');

var PluginsStore = di.get('stores/plugins');

var MULTI_FIELD_DELIM = '.';

/**
 * Descriptor of how to relay a single field
 * @typedef {Object} RelayField
 * @property {string} name Name of field in memory
 * @property {string} relayName Name of field in relay media (must match between sending/receiving snippets
 * @property {Function} [valueToValueString] Function mapping field value to "persistable" field value for relay (default `String`).
 * @property {Function} [valueFromValueString] Function mapping persisted field value to in-memory field value (default `_.identity`).
 * @property {Function} [encodeValueString] Function mapping value string (from `valueToValueString`) to encoded value string (default `encodeURIComponent`).
 * @property {Function} [decodeValueString] Function mapping value string (from `valueFromValueString`) to decoded value string (default `Cookie.safeDecodeURIComponent`).
 * @property {boolean} [isNullable] Whether the field value is allowed to be null (default `false`).
 *  During serialization, a null value will not be written to the relay medium.
 *  During deserialization, the null value will be reconstructed if the field is missing in the relay medium.
 * @property {boolean} [isMulti] Whether the field value is a "multi" mapping type. If true then all settings apply to each *subfield* within the map and `relayName` defines the prefix for field names (default `false`).
 */

/**
 * Field definitions
 * @type {module:analytics/relay~RelayField[]}
 */
var FIELDS = [
  // Visitor state that we want to stop tracking (the red fields in the CJS-1089 design doc)
  {
    name: 'sessionId',
    relayName: 's',
  },
  {
    name: 'decisionTicketAudienceIds',
    relayName: 'as',
    // Ensure that individual audience IDs, but not the comma delimiters, are encoded.
    valueToValueString: function(decisionTicketAudienceIds) {
      return _.map(decisionTicketAudienceIds, encodeURIComponent).join(',');
    },
    encodeValueString: _.identity,
    // Call safeDecodeURIComponent on individual audience IDs after splitting on the comma delimiter.
    // Don't decode before splitting on commas, in case we decode values that happens to include commas themselves
    // (not that we really need to expect those in audience IDs...).
    decodeValueString: _.identity,
    valueFromValueString: function(actuallyEncodedString) {
      return _.map(actuallyEncodedString.split(','), Cookie.safeDecodeURIComponent);
    },
  },

  // Visitor state (the orange fields in the CJS-1089 design doc)
  {
    name: 'decisionId',
    relayName: 'd',
  },
  {
    name: 'activationId',
    relayName: 'aId',
  },
  {
    name: 'pageId',
    relayName: 'vId',
    isNullable: true,
  },
  {
    name: 'variationId',
    relayName: 'v',
    isNullable: true,
  },
  {
    name: 'referrer',
    relayName: 'r',
  },
  {
    name: 'timestamp',
    relayName: 't',
    valueFromValueString: Number,
  },
  // Not included in cookieFields.
  {
    name: 'visitorId',
    relayName: 'i',
  },
  {
    name: 'projectId',
    relayName: 'p',
  },

  // Customer metadata (the yellow fields in the CJS-1089 design doc)
  {
    name: 'revision',
    relayName: 'n',
  },
  {
    name: 'clientName',
    relayName: 'cN',
    isNullable: true,
  },
  {
    name: 'clientVersion',
    relayName: 'cV',
  },
  {
    name: 'namespace',
    relayName: 'ns',
  },
  {
    name: 'accountId',
    relayName: 'a',
  },
  {
    name: 'layerId',
    relayName: 'l',
  },
  {
    name: 'layerName',
    relayName: 'lN',
    isNullable: true,
  },
  {
    name: 'layerPolicy',
    relayName: 'lP',
  },
  {
    name: 'experimentId',
    relayName: 'x',
    isNullable: true,
  },
  {
    name: 'experimentName',
    relayName: 'xN',
    isNullable: true,
  },
  {
    name: 'variationName',
    relayName: 'vN',
    isNullable: true,
  },
  {
    name: 'integrationStringVersion',
    relayName: 'isv',
    valueFromValueString: Number,
    isNullable: true,
  },
  {
    name: 'integrationSettings',
    relayName: 'iS',
    isMulti: true,
    valueToValueString: makePersistableIntegrationSettings,
    valueFromValueString: parsePersistedIntegrationSettings,
    isNullable: true,
  },
];

var FIELDS_BY_NAME = {} as any;
var FIELDS_BY_RELAY_NAME = {} as any;
_.forEach(FIELDS, function(field) {
  if (__INVARIANT__) {
    if (_.has(FIELDS_BY_NAME, field.name)) {
      throw new Error('Relay / Multiple fields with same name: ' + field.name);
    }
    if (_.has(FIELDS_BY_NAME, field.relayName)) {
      throw new Error('Relay / Multiple fields with same relayName: ' + field.relayName);
    }
    if (_.includes(field.relayName, MULTI_FIELD_DELIM)) {
      throw new Error('Relay / Field relayName contains isMulti delimiter: ' + field.relayName);
    }
  }
  FIELDS_BY_NAME[field.name] = field;
  FIELDS_BY_RELAY_NAME[field.relayName] = field;
});

/**
 * Persist data to supported Redirect Relay Media according to the definitions in FIELDS
 * @param {PersistableDecisionWithContext} data
 * @param {trackingPolicies.RedirectRelayMedia} redirectRelayMedium
 */
exports.persist = function(data:PersistableDecisionWithContext, redirectRelayMedium:import('core/constants/tracking_policies' ).RedirectRelayMedia) {
  if (__REDIRECT_RELAY_MEDIA__.COOKIE && redirectRelayMedium === trackingPolicies.RedirectRelayMedia.COOKIE) {
    persistToCookie(data);
  } else {
    // TODO: Add `else if` blocks for other relay media.
    Logger.error('Relay / Unsupported redirect relay medium: ' + redirectRelayMedium);
  }
};

/**
 * @param {PersistableDecisionWithContext} data
 * @private
 */
function persistToCookie(data:PersistableDecisionWithContext) {
  try {
    var cookieValue = makePersistableString(data);
  } catch (e) {
    Logger.error('Relay / Error computing redirect relay cookie: ', e);
    CoreApiEvents.emitError(e);
    return;
  }

  Logger.debug('Relay / Setting redirect relay cookie:', cookieValue);
  try {
    Cookie.set(
      enums.COOKIES.REDIRECT,
      cookieValue,
      {
        maxAge: __REDIRECT_COOKIE_TIMEOUT_SECONDS__,
        encodeValue: false,
      }
    );
  } catch(e) {
    Logger.error('Relay / Failed to set redirect relay cookie', e);
    CoreApiEvents.emitError(e);
  }
}

/**
 * @param {PersistableDecisionWithContext} data
 * @returns {PersistableLayerDecisionString}
 * @throws {Error} if there were errors in stringifying or encoding any fields, or if some required fields were missing.
 * @private
 */
function makePersistableString(data:PersistableDecisionWithContext): PersistableLayerDecisionString {
  var unrecognizedFieldNames = [];

  var segments = _.reduce(data, function(res, fieldValue, fieldName) {
    var field = FIELDS_BY_NAME[fieldName];
    if (!field) {
      unrecognizedFieldNames.push(fieldName);
      return res;
    }

    if (field.isMulti) {
      _.forEach(fieldValue, function(subFieldValue, subFieldName) {
        if (field.valueToValueString) {
          subFieldValue = field.valueToValueString(subFieldValue, subFieldName);
        } else {
          subFieldValue = String(subFieldValue);
        }
        if (!_.isNull(subFieldValue)) {
          subFieldValue = (field.encodeValueString || encodeURIComponent)(subFieldValue);
          res.push(encodeURIComponent(field.relayName + MULTI_FIELD_DELIM + subFieldName) + '=' + subFieldValue);
        }
      });
    } else if (!_.isNull(fieldValue)) {
      fieldValue = (field.valueToValueString || String)(fieldValue);
      fieldValue = (field.encodeValueString || encodeURIComponent)(fieldValue);
      res.push(field.relayName + '=' + fieldValue);
    }

    return res;
  }, []);

  if (unrecognizedFieldNames.length) {
    // We shouldn't relay the fields that we *did* recognize fields, since they're probably insufficient according to
    // this client's definition of PersistableDecisionWithContext.
    throw new Error('Relay / Don\'t know how to relay some fields: ' + unrecognizedFieldNames);
  }

  // Sort the segments so that the final string is deterministic enough for testing purposes.
  segments.sort();

  return segments.join('&');
}

/**
 * Look in the various supported Redirect Relay Media for any data from a previous page load,
 * according to the definitions in FIELDS.
 * It doesn't matter what the current snippet specifies as its Redirect Relay Medium.
 *
 * @param {string} [persistedString] The raw relayed string if loaded via the experimental push API or similar.
 * @returns {PersistableDecisionWithContext|undefined}
 *   May be a *partial* PersistableDecisionWithContext object if data was found with missing or invalid fields.
 *   This object may be unsatisfactory to our tracker plugins, in which case they may gracefully reject the data,
 *   or send invalid network requests, or (worst case) cause problems with a third-party script.
 */
exports.load = function(persistedString?:string): PersistableDecisionWithContext|undefined {
  // TODO: Add support for other relay media.
  if (!persistedString) {
    persistedString = loadPersistedStringFromCookie();
  }
  if (!persistedString) {
    return;
  }

  var data = parsePersistedString(persistedString);
  if (!data) {
    return;
  }

  // Confirm that we've parsed a valid PersistableDecisionWithContext object.
  var missingFields = [];
  _.forEach(FIELDS, function(field) {
    if (_.isNull(data[field.name]) || _.isUndefined(data[field.name])) {
      if (field.isNullable) {
        data[field.name] = null;
      } else {
        delete data[field.name];
        missingFields.push(field.name);
      }
    }
  });
  if (missingFields.length) {
    Logger.error('Relay / Observed redirect data with missing fields:', missingFields);
    return;
  }

  Flux.dispatch(actionTypes.LOAD_REDIRECT_DATA, data);
  // Recreate the pre-redirect layer state *after* the LayerStore has been initialized from localStorage.
  Flux.dispatch(actionTypes.ADD_CLEANUP_FN, {
    lifecycle: enums.Lifecycle.postVisitorProfileLoad,
    cleanupFn: function() {
      recordRedirectLayerState(data);
    },
  });
  return data;
};

/**
 * This function assumes the cookie is stored in the new format, so it shouldn't be called if we can tell we've found a
 * legacy cookie.
 *
 * @returns {string|undefined}
 * @private
 */
function loadPersistedStringFromCookie(): string|undefined {
  var cookieString = Cookie.get(enums.COOKIES.REDIRECT, false);
  if (cookieString) {
    Logger.log('Relay / Found redirect cookie:', cookieString);
    return cookieString;
  }
}

/**
 * @param {PersistableLayerDecisionString} string
 * @returns {PersistableDecisionWithContext}
 *   May be a *partial* PersistableDecisionWithContext object if there were missing or invalid fields.
 *   This object may be unsatisfactory to our tracker plugins, in which case they may gracefully reject the data,
 *   or send invalid network requests, or (worst case) cause problems with a third-party script.
 * @private
 */
function parsePersistedString(string:PersistableLayerDecisionString): PersistableDecisionWithContext {
  var data = {} as any;

  var segments = string.split('&');
  _.forEach(segments, function(segment) {
    var segmentSegments = segment.split('=');
    if (segmentSegments.length !== 2) {
      Logger.warn('Relay / Skipping invalid segment:', segment);
      // Skip this segment (but continue parsing the remaining ones).
      return;
    }

    // TODO: Use _.findKey if we ever add that to optimizely-client-fn
    var fieldRelayName = Cookie.safeDecodeURIComponent(segmentSegments[0]);
    var field = FIELDS_BY_RELAY_NAME[fieldRelayName];
    if (!field) {
      // check for "multi" fields
      field = _.find(FIELDS, function(eachField) {
        return eachField.isMulti && fieldRelayName.indexOf(eachField.relayName + MULTI_FIELD_DELIM) === 0;
      });

      if (!field) {
        // This could very well occur if the string was persisted by a snippet with a different clientName or
        // clientVersion (and therefore a different PersistableDecisionWithContext schema).
        Logger.warn('Relay / Skipping segment with unknown field identifier:', segment, fieldRelayName);
        return;
      }
    }

    var fieldValue = segmentSegments[1];
    try {
      if (field.isMulti) {
        data[field.name] = data[field.name] || {};
        var subFieldName = fieldRelayName.substring(field.relayName.length + MULTI_FIELD_DELIM.length);
        fieldValue = (field.decodeValueString || Cookie.safeDecodeURIComponent)(fieldValue);
        fieldValue = (field.valueFromValueString || _.identity)(fieldValue, subFieldName);
        data[field.name][subFieldName] = fieldValue;
      } else {
        fieldValue = (field.decodeValueString || Cookie.safeDecodeURIComponent)(fieldValue);
        fieldValue = (field.valueFromValueString || _.identity)(fieldValue);
        data[field.name] = fieldValue;
      }
    } catch(e) {
      Logger.warn('Relay / Skipping segment due to decode or parse error:', segment, e);
      CoreApiEvents.emitError(e);
      // Skip this segment (but continue parsing the remaining ones).
      return;
    }
  });

  return data;
}

/**
 * @param {*} integrationSettings Settings value
 * @param {string} pluginName
 * @return {string|null} Persistable string for settings, or undefined if no persistable settings
 * @private
 */
function makePersistableIntegrationSettings(integrationSettings:any, pluginName:string): string|null {
  var persistableVal = null;
  if (integrationSettings) {
    var plugin = PluginsStore.getPlugin(enums.PluginTypes.analyticsTrackers, pluginName);
    if (plugin && _.isFunction(plugin.serializeSettings)) {
      try {
        persistableVal = plugin.serializeSettings(integrationSettings);
      } catch(e) {
        Logger.warn('Analytics / Failed to persist integrationSettings for plugin:', pluginName, e);
      }
    }
  }
  return persistableVal;
}

/**
 * @param {string} formattedIntegrationSettings Persisted string
 * @param {string} pluginName
 * @return {*|null} Settings value, or undefined if not parseable
 * @private
 */
function parsePersistedIntegrationSettings(formattedIntegrationSettings:string, pluginName:string): any|null {
  var parsedVal = null;
  var plugin = PluginsStore.getPlugin(enums.PluginTypes.analyticsTrackers, pluginName);
  if (plugin && _.isFunction(plugin.deserializeSettings)) {
    try {
      parsedVal = plugin.deserializeSettings(formattedIntegrationSettings);
    } catch(e) {
      Logger.warn('Analytics / Failed to persist integrationSettings for plugin:', pluginName, e);
    }
  }
  return parsedVal;
}

/**
 * Persist a similar layer state to that which was persisted on the pre-redirect page, since the pre-redirect page
 * performed a synchronous redirect and wasn't able to sync its layer state in its localStorage to x-origin storage,
 * preventing this page from accessing the original layer state if we're running on a different origin.
 *
 * TODO: Maybe only do this after an *x-origin* redirect?
 *
 * TODO: Figure out which layer state (pre-redirect vs. post-redirect) will "win" if they were to collide
 *       after a same-origin redirect or due to x-origin syncing.
 *       Maybe avoid the problem by only writing redirect experiences' layer state data on the post-redirect page?
 *
 * @param {PersistableDecisionWithContext} persistedData
 * @private
 */
function recordRedirectLayerState(persistedData:PersistableDecisionWithContext) {
  var pageId = persistedData.pageId || undefined;

  Flux.dispatch(actionTypes.RECORD_LAYER_DECISION, {
    layerId: persistedData.layerId,
    decision: {
      layerId: persistedData.layerId,
      experimentId: persistedData.experimentId,
      variationId: persistedData.variationId,
      isLayerHoldback: false,
    },
    decisionTicket: {
      audienceIds: persistedData.decisionTicketAudienceIds,
      bucketingId: persistedData.visitorId,
      globalHoldback: 0,
      // This field isn't relayed from the pre-redirect page.
      // Fortunately, it's not really used. For sticky bucketing, we refer to the VisitorIdStore (backed by the
      // variation_map key in localStorage) rather than the LayerStore (backed by the layer_states key in localStorage).
      preferredVariationMap: undefined,
      pageId: pageId,
      activationId: persistedData.activationId,
    },
    sessionId: persistedData.sessionId,
    activationId: persistedData.activationId,
    timestamp: persistedData.timestamp,
    revision: persistedData.revision,
    namespace: persistedData.namespace,
    pageId: pageId,
  });

  Flux.dispatch(actionTypes.RECORD_LAYER_DECISION_EVENT_ID, {
    layerId: persistedData.layerId,
    pageId: pageId,
    decisionId: persistedData.decisionId,
  });

  Flux.dispatch(actionTypes.ACTION_EXECUTED, {
    sessionId: persistedData.sessionId,
    layerId: persistedData.layerId,
    pageId: persistedData.pageId,
    timestamp: persistedData.timestamp,
    activationId: persistedData.activationId,
  });
}
